import React, { useCallback, useState } from "react";
import { toast } from "react-toastify";
import Services from "../service/Services";
import Spinner from "../components/Spinner";
import { HiOutlineTrash, HiOutlineRefresh } from "react-icons/hi";

const PAGE_SIZE = 20;
// Start with ALL so existing logs (including rate-limit, auth, etc.) always appear;
// user can narrow down to \"Mail not found\" from the filter dropdown.
const DEFAULT_TYPE = "ALL";

const TYPE_OPTIONS = [
  { value: "MAIL_NOT_FOUND", label: "Mail not found" },
  { value: "AUTH_FAILED", label: "Auth failed" },
  { value: "RATE_LIMIT", label: "Rate limit / quota" },
  { value: "CONNECTION", label: "Connection / DNS" },
  { value: "TIMEOUT", label: "Timeout" },
  { value: "BLOCKED", label: "Blocked / Spam" },
  { value: "ALL", label: "All failed" },
];

function formatDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleString("en-IN", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

export default function FailedEmailLogs() {
  const [list, setList] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [typeFilter, setTypeFilter] = useState(DEFAULT_TYPE);
  const [deactivateConfirmOpen, setDeactivateConfirmOpen] = useState(false);
  const [deactivating, setDeactivating] = useState(false);

  const fetchFailed = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const res = await Services.EmailLogs.getFailed({
          page: String(page),
          limit: String(PAGE_SIZE),
          type: typeFilter,
        });
        if (res.success && res.data) {
          setList(res.data);
          setPagination(res.pagination || { page: 1, limit: PAGE_SIZE, total: 0, totalPages: 0 });
          setSelectedIds(new Set());
        } else {
          setList([]);
          setPagination((p) => ({ ...p, total: 0, totalPages: 0 }));
        }
      } catch (err) {
        console.error(err);
        toast.error(err.response?.data?.message || "Failed to load failed emails");
        setList([]);
      } finally {
        setLoading(false);
      }
    },
    [typeFilter]
  );

  React.useEffect(() => {
    fetchFailed(pagination.page);
  }, [pagination.page, fetchFailed]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === list.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(list.map((row) => row._id)));
    }
  };

  const handleDeactivateSelected = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) {
      toast.warn("Select at least one row to deactivate.");
      return;
    }
    setDeactivating(true);
    try {
      const res = await Services.EmailLogs.deactivate({ ids });
      if (res.success) {
        toast.success(`Deactivated ${res.modifiedCount ?? ids.length} record(s).`);
        setDeactivateConfirmOpen(false);
        setSelectedIds(new Set());
        await fetchFailed(pagination.page);
      } else {
        toast.error(res.message || "Deactivate failed");
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || err.message || "Deactivate failed");
    } finally {
      setDeactivating(false);
    }
  };

  const openDeactivateConfirm = () => {
    if (selectedIds.size === 0) {
      toast.warn("Select at least one row to deactivate.");
      return;
    }
    setDeactivateConfirmOpen(true);
  };

  const allSelected = list.length > 0 && selectedIds.size === list.length;
  const someSelected = selectedIds.size > 0;

  const deactivateAllMatching = async () => {
    if (pagination.total === 0) {
      toast.info("No matching failed emails to deactivate.");
      return;
    }
    const ok = window.confirm(
      `Deactivate ALL matching failed emails (${pagination.total}) for filter: ${TYPE_OPTIONS.find((o) => o.value === typeFilter)?.label || typeFilter}?`
    );
    if (!ok) return;
    setDeactivating(true);
    try {
      const res = await Services.EmailLogs.deactivate({ filter: { type: typeFilter } });
      if (res.success) {
        toast.success(`Deactivated ${res.modifiedCount ?? 0} record(s).`);
        setSelectedIds(new Set());
        setPagination((p) => ({ ...p, page: 1 }));
        await fetchFailed(1);
      } else {
        toast.error(res.message || "Deactivate failed");
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || err.message || "Deactivate failed");
    } finally {
      setDeactivating(false);
    }
  };

  return (
    <div className="flex flex-col gap-6 w-full">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-800">Failed Emails</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Email log entries with status &quot;Failed&quot;. Select and delete to remove from database.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value);
              setPagination((p) => ({ ...p, page: 1 }));
            }}
            className="px-3 py-2 rounded-lg border border-slate-300 bg-white text-slate-700 text-sm font-medium"
            aria-label="Filter by failure type"
            disabled={loading}
          >
            {TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => fetchFailed(pagination.page)}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 bg-white text-slate-700 text-sm font-medium hover:bg-slate-50 disabled:opacity-50"
          >
            <HiOutlineRefresh className="w-4 h-4" />
            Refresh
          </button>
          <button
            type="button"
            onClick={deactivateAllMatching}
            disabled={loading || deactivating}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 text-sm font-medium hover:bg-amber-100 disabled:opacity-50"
            title="Deactivate all failed logs matching the current filter (across pages)"
          >
            Deactivate all ({pagination.total})
          </button>
          {someSelected && (
            <button
              type="button"
              onClick={openDeactivateConfirm}
              disabled={deactivating}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-red-300 bg-red-50 text-red-700 text-sm font-medium hover:bg-red-100 disabled:opacity-50"
            >
              <HiOutlineTrash className="w-4 h-4" />
              Deactivate selected ({selectedIds.size})
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {loading && <Spinner />}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="w-10 px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleSelectAll}
                    className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                    aria-label="Select all"
                  />
                </th>
                <th className="px-4 py-3 text-left font-semibold text-slate-700">Email</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-700">Subject</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-700">Sent at</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-700">Service</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-700 max-w-xs">Error / Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              {!loading && list.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-slate-500">
                    No failed email logs found.
                  </td>
                </tr>
              )}
              {list.map((row) => (
                <tr key={row._id} className="hover:bg-slate-50">
                  <td className="w-10 px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(row._id)}
                      onChange={() => toggleSelect(row._id)}
                      className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                      aria-label={`Select ${row.email}`}
                    />
                  </td>
                  <td className="px-4 py-3 text-slate-800">{row.email || "—"}</td>
                  <td className="px-4 py-3 text-slate-800">{row.subject || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{formatDate(row.sentAt)}</td>
                  <td className="px-4 py-3 text-slate-600">
                    {row.serviceId?.name || row.provider || "—"}
                  </td>
                  <td className="px-4 py-3 text-slate-600 max-w-xs truncate" title={row.errorDetails || ""}>
                    {row.errorDetails || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50 text-sm text-slate-600">
            <span>
              Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPagination((p) => ({ ...p, page: Math.max(1, p.page - 1) }))}
                disabled={pagination.page <= 1 || loading}
                className="px-3 py-1 rounded border border-slate-300 bg-white disabled:opacity-50 hover:bg-slate-100"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() =>
                  setPagination((p) => ({ ...p, page: Math.min(p.totalPages, p.page + 1) }))
                }
                disabled={pagination.page >= pagination.totalPages || loading}
                className="px-3 py-1 rounded border border-slate-300 bg-white disabled:opacity-50 hover:bg-slate-100"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {deactivateConfirmOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-confirm-title"
        >
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h2 id="delete-confirm-title" className="text-lg font-semibold text-slate-800 mb-2">
              Deactivate failed logs?
            </h2>
            <p className="text-slate-600 mb-6">
              This will mark {selectedIds.size} failed email log(s) as inactive, so they won’t show up by default.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => !deactivating && setDeactivateConfirmOpen(false)}
                disabled={deactivating}
                className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeactivateSelected}
                disabled={deactivating}
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-2"
              >
                {deactivating ? "Deactivating…" : "Deactivate"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
